import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bankIdApi, localBankIdDebugApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalBankIdDebug } from '../context/LocalBankIdDebugContext';
import { authenticatedHomePath, requiresOnboarding, requiresEmailVerification } from '../utils/onboarding';
import type { AuthResponse, BankIdAuthStart, User } from '../types';
import { BankIdFlow } from './BankIdFlow';

const personas = ['person', 'company', 'lawyer', 'inspector'] as const;

function CapabilityUnavailable() {
  const { t } = useTranslation();
  const { retry } = useLocalBankIdDebug();
  return <div className="alert alert-warning" role="alert">
    <p>{t('localBankIdDebug.unavailable')}</p>
    <button type="button" className="btn btn-outline-dark btn-lg" onClick={retry}>{t('localBankIdDebug.retry')}</button>
  </div>;
}

export function LocalBankIdDebugForm({ request, onContinue, onBusyChange }: { request: BankIdAuthStart; onContinue: (response: AuthResponse) => Promise<void>; onBusyChange?: (busy: boolean) => void }) {
  const { t } = useTranslation();
  const { actors, loading, unavailable } = useLocalBankIdDebug();
  const { user } = useAuth();
  const personaLabel = (value: string) => t(`localBankIdDebug.personas.${value === 'person' && user ? 'personSignedIn' : value}`);
  const prefix = useId();
  const [actorId, setActorId] = useState('');
  const [fullName, setFullName] = useState('');
  const [persona, setPersona] = useState<BankIdAuthStart['personaType']>('person');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  if (loading) return <p role="status">{t('common.loading')}</p>;
  if (unavailable) return <CapabilityUnavailable />;
  const registering = request.purpose === 'register';
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(false); onBusyChange?.(true);
    let session: { id: string; sessionToken: string } | null = null;
    try {
      const started = await localBankIdDebugApi.start({ purpose: request.purpose, invitationId: request.invitationId,
        ...(registering ? { fullName: fullName.trim(), personaType: request.personaType ?? persona } : { actorId }) });
      if (!started.sessionToken) throw new Error('Missing browser ownership');
      session = { id: started.id, sessionToken: started.sessionToken };
      const response = await bankIdApi.complete(session.id, session.sessionToken);
      if (active.current) await onContinue(response);
    } catch {
      if (session) await bankIdApi.cancel(session.id, session.sessionToken).catch(() => {});
      if (active.current) setError(true);
    } finally { if (active.current) { setBusy(false); onBusyChange?.(false); } }
  };
  return <form onSubmit={event => void submit(event)} className="local-bankid-debug">
    <p className="small text-muted">{t('localBankIdDebug.description')}</p>
    {error && <p className="alert alert-danger" role="alert">{t('localBankIdDebug.error')}</p>}
    {registering ? <>
      <label className="form-label" htmlFor={`${prefix}-name`}>{t('localBankIdDebug.name')}</label>
      <input id={`${prefix}-name`} className="form-control form-control-lg mb-3" value={fullName} maxLength={255} required disabled={busy} onChange={event => setFullName(event.target.value)} autoComplete="off" />
      {!request.personaType && <>
        <label className="form-label" htmlFor={`${prefix}-persona`}>{t('localBankIdDebug.persona')}</label>
        <select id={`${prefix}-persona`} className="form-select form-select-lg mb-3" value={persona} disabled={busy} onChange={event => setPersona(event.target.value as BankIdAuthStart['personaType'])}>
          {personas.map(value => <option key={value} value={value}>{personaLabel(value)}</option>)}
        </select>
      </>}
    </> : <>
      <label className="form-label" htmlFor={`${prefix}-actor`}>{t('localBankIdDebug.actor')}</label>
      <select id={`${prefix}-actor`} className="form-select form-select-lg mb-3" value={actorId} disabled={busy} required onChange={event => setActorId(event.target.value)}>
        <option value="">{t('localBankIdDebug.choose')}</option>
        {actors.map(actor => <option key={actor.id} value={actor.id}>{t('localBankIdDebug.actorOption', { name: actor.fullName, role: personaLabel(actor.isAdmin ? 'admin' : actor.personaType), email: actor.email ?? t('localBankIdDebug.noEmail') })}</option>)}
      </select>
      {!actors.length && <p role="status">{t('localBankIdDebug.empty')}</p>}
    </>}
    <button className="btn btn-primary btn-lg w-100" type="submit" disabled={busy || (registering ? !fullName.trim() : !actorId)}>{t(busy ? 'common.loading' : registering ? 'localBankIdDebug.register' : 'localBankIdDebug.login')}</button>
  </form>;
}

/** The server selects the available methods before any BankID session starts. */
export function BankIdEntry(props: { request: BankIdAuthStart; onContinue: (response: AuthResponse) => Promise<void>; onBusyChange?: (busy: boolean) => void }) {
  const { t } = useTranslation();
  const debug = useLocalBankIdDebug();
  const [useTic, setUseTic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const onBusyChange = (busy: boolean) => { setSubmitting(busy); props.onBusyChange?.(busy); };
  if (debug.loading) return <p role="status">{t('common.loading')}</p>;
  if (debug.unavailable || (debug.environment === 'testserver' && !debug.enabled)) return <CapabilityUnavailable />;
  if (!debug.enabled || ['staging', 'production'].includes(debug.environment ?? '')) return <BankIdFlow {...props} />;
  const ticSelected = debug.allowTic && useTic;
  return <>
    {debug.allowTic && <div className="d-flex flex-column flex-sm-row gap-2 mb-3" role="group" aria-label={t('localBankIdDebug.method')}>
      <button type="button" className={`btn btn-lg flex-fill ${useTic ? 'btn-outline-dark' : 'btn-dark'}`} disabled={submitting} aria-pressed={!useTic} onClick={() => setUseTic(false)}>{t('localBankIdDebug.testActors')}</button>
      <button type="button" className={`btn btn-lg flex-fill ${useTic ? 'btn-dark' : 'btn-outline-dark'}`} disabled={submitting} aria-pressed={useTic} onClick={() => setUseTic(true)}>{t('localBankIdDebug.tic')}</button>
    </div>}
    {debug.environment === 'testserver' && <p className="alert alert-warning">{t('localBankIdDebug.simulatedBankId')}</p>}
    {ticSelected ? <>
      <p className="small text-muted">{t('localBankIdDebug.ticDescription')}</p>
      <BankIdFlow {...props} onBusyChange={onBusyChange} />
    </> : <LocalBankIdDebugForm {...props} onBusyChange={onBusyChange} />}
  </>;
}

export function testActorDestination(path: string, user: User): string {
  if (requiresOnboarding(user) || requiresEmailVerification(user)) return authenticatedHomePath(user);
  const match = path.match(/^\/(?:admin\/|person\/|lawyer\/|inspector\/)?projects\/([0-9a-f-]{36})([/?#].*)?$/i);
  if (!match) return authenticatedHomePath(user);
  const prefix = user.isAdmin || user.personaType === 'admin' ? '/admin/projects' :
    ['person', 'lawyer', 'inspector'].includes(user.personaType) ? `/${user.personaType}/projects` : '/projects';
  return `${prefix}/${match[1]}${match[2] ?? ''}`;
}
